/**
 * CMSColorTest for Specific Theme
 * This component generates and displays CMS colors for a specific theme
 * Used in the fullscreen modal when clicking the "Test Colors" button
 */

import React from "react"
import { api } from "aldoo-ra/API"
import Input from "aldoo-ra/Input"

const siteThemeAPI = api("site-theme")

const CMSColorTestSpecific = ({ theme, onColorsLoaded }) => {
  const [colorsData, setColorsData] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [search, setSearch] = React.useState("")

  // Fetch color data for the specific theme (without applying CSS)
  React.useEffect(() => {
    if (!theme?._id) return

    const fetchColorsForTheme = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await siteThemeAPI({
          get_cms_color_stylesheet: { 
            themeId: theme._id 
          }
        })

        if (response.result) {
          // Just extract the colors data, don't apply any CSS
          const colors = response.result.colors || []
          setColorsData(colors)
          onColorsLoaded?.(colors.length)
        } else {
          setError('Failed to fetch colors for theme')
        }
      } catch (error) {
        console.error('Failed to fetch colors for theme:', error)
        setError(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchColorsForTheme()
  }, [theme])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text_secondary">Generating colors for {theme.name}...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error/10 border border-error/20 rounded-lg p-6">
          <h2 className="text-xl font-bold text-error mb-2">
            Error Loading Colors
          </h2>
          <p className="text-text_secondary">{error}</p>
        </div>
      </div>
    )
  }

  if (colorsData.length === 0) {
    return (
      <div className="p-8">
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-6">
          <h2 className="text-xl font-bold text-warning mb-2">
            No Colors Found
          </h2>
          <p className="text-text_secondary">
            This theme doesn't have any colors defined.
          </p>
        </div>
      </div>
    )
  }

  const filtered = search.trim()
    ? colorsData.filter(n => n.toLowerCase().includes(search.trim().toLowerCase()))
    : colorsData

  return (
    <div className="p-8 space-y-6 bg-canvas">
      <div className="max-w-6xl mx-auto">

        {/* Search */}
        <div className="mb-4 flex flex-col items-center gap-2">
          <Input
            variant="admin"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search colors..."
            className="w-full max-w-sm"
            showClearButton={!!search}
            onClear={() => setSearch("")}
          />
          {search && (
            <span className="text-sm text-admin_text_secondary">
              {filtered.length} / {colorsData.length}
            </span>
          )}
        </div>

        {/* Color Grid */}
        <div
          className="bg-paper rounded-lg p-6 shadow-lg mb-6"
          style={{
            backgroundImage: `
              linear-gradient(45deg, #f0f0f0 25%, transparent 25%), 
              linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #f0f0f0 75%), 
              linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
            `,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
          }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((colorName, index) => {
              // Get the actual color value from the theme, fallback to CSS variable (for "Site Default" theme)
              let colorValue = Array.isArray(theme.colors)
                ? theme.colors.find(c => c.key === colorName)?.value
                : theme.colors?.[colorName]
              if (!colorValue && typeof window !== 'undefined') {
                const cssVar = getComputedStyle(document.documentElement).getPropertyValue(`--color-${colorName}`).trim()
                if (cssVar) colorValue = cssVar
              }
              let backgroundColor = '#cccccc' // Default gray
              let textColor = 'white'
              
              if (colorValue) {
                // Handle different color formats
                if (colorValue.startsWith('#')) {
                  // Hex format
                  backgroundColor = colorValue
                } else if (colorValue.includes(' ')) {
                  // RGB space-separated format like "255 0 0"
                  backgroundColor = `rgb(${colorValue})`
                } else if (colorValue.includes(',')) {
                  // RGB comma-separated format like "255, 0, 0"
                  backgroundColor = `rgb(${colorValue})`
                } else {
                  // Try to use as-is
                  backgroundColor = colorValue
                }
                
                // Calculate text color based on background brightness
                try {
                  let r, g, b
                  
                  if (colorValue.startsWith('#')) {
                    // Parse hex
                    const hex = colorValue.replace('#', '')
                    if (hex.length === 6) {
                      r = parseInt(hex.substr(0, 2), 16)
                      g = parseInt(hex.substr(2, 2), 16)
                      b = parseInt(hex.substr(4, 2), 16)
                    }
                  } else if (colorValue.includes(' ')) {
                    // Parse RGB space-separated
                    const rgbValues = colorValue.split(' ').map(v => parseInt(v.trim())).filter(v => !isNaN(v))
                    if (rgbValues.length === 3) {
                      [r, g, b] = rgbValues
                    }
                  }
                  
                  if (r !== undefined && g !== undefined && b !== undefined) {
                    // Calculate luminance
                    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
                    textColor = luminance > 0.5 ? 'black' : 'white'
                  }
                } catch (error) {
                  textColor = 'white'
                }
              }
              
              return (
                <div 
                  key={index}
                  className="relative rounded shadow-sm min-h-[100px] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-black/50"
                  style={{
                    backgroundImage: `
                      linear-gradient(45deg, #ccc 25%, transparent 25%), 
                      linear-gradient(-45deg, #ccc 25%, transparent 25%), 
                      linear-gradient(45deg, transparent 75%, #ccc 75%), 
                      linear-gradient(-45deg, transparent 75%, #ccc 75%)
                    `,
                    backgroundSize: '10px 10px',
                    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                  }}
                >
                  <div 
                    className="p-4 rounded min-h-[100px] flex flex-col justify-center"
                    style={{
                      backgroundColor,
                      color: textColor
                    }}
                  >
                    <div className="text-sm font-semibold break-words">bg-{colorName}</div>
                    <div className="text-xs opacity-75 capitalize break-words mt-1">
                      {colorName.replace(/_/g, ' ')}
                    </div>
                    <div className="text-xs opacity-50 mt-1 font-mono">
                      {colorValue || 'undefined'}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>


      </div>
    </div>
  )
}

export default CMSColorTestSpecific